/**
 * Fidelity custom storefront scripts.
 */

/* ---------------------------------------------------------------------------
 * Klaviyo Back-in-Stock
 * Concept theme is not detected by Klaviyo auto-replace, and openForm alone
 * often fails with "Product Variant does not exist" (empty variants JSON).
 * We open the live Klaviyo form for UI, then create the real subscription via
 * the client BIS API using the Shopify variant id from the product form.
 * --------------------------------------------------------------------------- */

const KLAVIYO_BIS_ENDPOINT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';
const KLAVIYO_BIS_REVISION = '2024-06-15';
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

let klaviyoBisFormIdPromise = null;
let klaviyoBisSubmitBound = false;
const klaviyoBisSubscribedEmails = new Set();

function getKlaviyoCompanyId() {
  for (const script of document.querySelectorAll('script[src*="klaviyo"]')) {
    const match = script.src.match(/company_id=([^&"'\\s]+)/);
    if (match) return decodeURIComponent(match[1]);
  }
  return window._klaviyo?.company_id || window.__klKey || null;
}

function getShopifyVariantId(trigger) {
  if (trigger?.dataset?.variantId) return String(trigger.dataset.variantId);

  const productForm = document.querySelector(
    'form.product-form[data-type="add-to-cart-form"], form[is="product-form"]'
  );
  const fromForm = productForm?.querySelector('input[name="id"]')?.value;
  if (fromForm) return String(fromForm);

  const fromPage = document.querySelector('input[name="id"]')?.value;
  return fromPage ? String(fromPage) : null;
}

function buildKlaviyoVariantId(shopifyVariantId) {
  return `$shopify:::$default:::${shopifyVariantId}`;
}

function syncVariantToUrl(variantId) {
  if (!variantId) return;
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.get('variant') === String(variantId)) return;
    url.searchParams.set('variant', String(variantId));
    window.history.replaceState({}, '', url);
  } catch {
    // Ignore URL sync failures.
  }
}

function resolveKlaviyoBisFormId() {
  if (klaviyoBisFormIdPromise) return klaviyoBisFormIdPromise;

  const companyId = getKlaviyoCompanyId();
  if (!companyId) {
    klaviyoBisFormIdPromise = Promise.resolve(null);
    return klaviyoBisFormIdPromise;
  }

  klaviyoBisFormIdPromise = fetch(
    `https://static-forms.klaviyo.com/forms/api/v7/${encodeURIComponent(companyId)}/full-forms`
  )
    .then((response) => (response.ok ? response.json() : null))
    .then((payload) => {
      const forms = payload?.full_forms || [];
      for (const form of forms) {
        const versions = form.live_form_versions || [];
        const isBis = versions.some((version) =>
          (version.form_specialties || []).includes('BACK_IN_STOCK')
        );
        if (isBis && form.form_id) return form.form_id;
      }
      return null;
    })
    .catch(() => null);

  return klaviyoBisFormIdPromise;
}

function ensureKlaviyoProductMeta() {
  const node = document.querySelector('[data-we-klaviyo-product]');
  if (!node) return;

  let product;
  try {
    product = JSON.parse(node.textContent);
  } catch {
    return;
  }

  if (!product?.id || !Array.isArray(product.variants)) return;

  window.meta = window.meta || {};
  window.meta.product = {
    ...(window.meta.product || {}),
    id: product.id,
    gid: `gid://shopify/Product/${product.id}`,
    vendor: product.vendor,
    type: window.meta.product?.type || '',
    variants: product.variants.map((variant) => ({
      id: variant.id,
      name: product.title,
      public_title: variant.title === 'Default Title' ? null : variant.title,
      sku: variant.sku || null,
      available: Boolean(variant.available),
      price: variant.price,
    })),
  };

  if (window.ShopifyAnalytics?.meta) {
    window.ShopifyAnalytics.meta.product = window.meta.product;
  }
}

function openKlaviyoForm(formId) {
  if (!formId) return false;

  ensureKlaviyoProductMeta();

  window.klaviyo = window.klaviyo || [];
  if (typeof window.klaviyo.openForm === 'function') {
    window.klaviyo.openForm(formId);
    return true;
  }

  window.klaviyo.push(['openForm', formId]);
  return true;
}

async function subscribeKlaviyoBackInStock(email, shopifyVariantId) {
  const companyId = getKlaviyoCompanyId();
  if (!companyId || !email || !shopifyVariantId) return false;

  const key = `${email.toLowerCase()}::${shopifyVariantId}`;
  if (klaviyoBisSubscribedEmails.has(key)) return true;

  const payload = {
    data: {
      type: 'back-in-stock-subscription',
      attributes: {
        channels: ['EMAIL'],
        profile: {
          data: {
            type: 'profile',
            attributes: { email },
          },
        },
      },
      relationships: {
        variant: {
          data: {
            type: 'catalog-variant',
            id: buildKlaviyoVariantId(shopifyVariantId),
          },
        },
      },
    },
  };

  try {
    const response = await fetch(
      `${KLAVIYO_BIS_ENDPOINT}?company_id=${encodeURIComponent(companyId)}`,
      {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          revision: KLAVIYO_BIS_REVISION,
        },
        body: JSON.stringify(payload),
      }
    );

    if (response.status === 202) {
      klaviyoBisSubscribedEmails.add(key);
      console.log('[Klaviyo BIS] Subscription accepted', {
        variantId: shopifyVariantId,
        note: 'Repeat submits for the same email + variant usually do not create new profile events.',
      });
      return true;
    }

    let detail = `HTTP ${response.status}`;
    try {
      const body = await response.json();
      detail = body?.errors?.[0]?.detail || detail;
    } catch {
      // Keep status detail.
    }
    console.warn('[Klaviyo BIS] Subscription failed', detail);
    return false;
  } catch (error) {
    console.warn('[Klaviyo BIS] Subscription request error', error);
    return false;
  }
}

function extractEmailFromKlaviyoForm(root = document) {
  const input = root.querySelector(
    'form.klaviyo-form input[name="email"], form.klaviyo-form input[type="email"]'
  );
  const email = input?.value?.trim();
  return EMAIL_PATTERN.test(email || '') ? email : null;
}

async function onKlaviyoBisTriggerClick(event) {
  event.preventDefault();
  event.stopPropagation();

  const trigger = event.currentTarget;
  const variantId = getShopifyVariantId(trigger);
  syncVariantToUrl(variantId);
  trigger.dataset.activeVariantId = variantId || '';

  const formId = await resolveKlaviyoBisFormId();
  if (!formId) {
    console.warn('[Klaviyo BIS] No live back-in-stock form found. Publish the form in Klaviyo.');
    return;
  }

  openKlaviyoForm(formId);
}

function bindKlaviyoBisSubmitFallback() {
  if (klaviyoBisSubmitBound) return;
  klaviyoBisSubmitBound = true;

  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest?.('form.klaviyo-form button');
      if (!button) return;

      const form = button.closest('form.klaviyo-form');
      if (!form) return;

      const email = extractEmailFromKlaviyoForm(form);
      const variantId =
        document.querySelector('.klaviyo-bis-trigger[data-active-variant-id]')?.dataset
          .activeVariantId || getShopifyVariantId();

      if (!email || !variantId) return;

      // Fire in parallel with Klaviyo's own handler; their UI path often fails
      // with "Product Variant does not exist" on this theme.
      subscribeKlaviyoBackInStock(email, variantId);
    },
    true
  );

  window.addEventListener('klaviyoForms', (event) => {
    const detail = event.detail || {};
    const type = detail.type;
    if (type !== 'submit' && type !== 'stepSubmit' && type !== 'redirectedToUrl') return;

    const email =
      detail.metaData?.$email ||
      detail.metaData?.email ||
      detail.email ||
      extractEmailFromKlaviyoForm();
    const variantId =
      document.querySelector('.klaviyo-bis-trigger[data-active-variant-id]')?.dataset
        .activeVariantId || getShopifyVariantId();

    if (!email || !variantId || !EMAIL_PATTERN.test(email)) return;
    subscribeKlaviyoBackInStock(email, variantId);
  });
}

function initKlaviyoBisTriggers(root = document) {
  root.querySelectorAll('.klaviyo-bis-trigger').forEach((trigger) => {
    if (trigger.dataset.weKlaviyoBisBound === 'true') return;
    trigger.dataset.weKlaviyoBisBound = 'true';
    trigger.addEventListener('click', onKlaviyoBisTriggerClick);
  });

  bindKlaviyoBisSubmitFallback();

  if (document.querySelector('.klaviyo-bis-trigger')) {
    resolveKlaviyoBisFormId();
  }
}

document.addEventListener('variant:change', (event) => {
  const variantId = event?.detail?.variant?.id || getShopifyVariantId();
  document.querySelectorAll('.klaviyo-bis-trigger').forEach((trigger) => {
    if (variantId) trigger.dataset.variantId = String(variantId);
  });
  initKlaviyoBisTriggers();
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initKlaviyoBisTriggers(), { once: true });
} else {
  initKlaviyoBisTriggers();
}

/* ---------------------------------------------------------------------------
 * Product card swatches: load exact PDP option colors via ?view=swatches
 * Collection cards cannot reliably resolve color_mapping for all products.
 * --------------------------------------------------------------------------- */

const cardSwatchCache = new Map();

function getCardSwatchRequestUrl(productUrl) {
  try {
    const url = new URL(productUrl, window.location.origin);
    url.search = '';
    url.searchParams.set('view', 'swatches');
    return url.pathname + url.search;
  } catch (error) {
    const base = String(productUrl || '').split('?')[0];
    return `${base}?view=swatches`;
  }
}

async function fetchCardSwatches(productUrl) {
  const requestUrl = getCardSwatchRequestUrl(productUrl);
  if (cardSwatchCache.has(requestUrl)) {
    return cardSwatchCache.get(requestUrl);
  }

  const request = fetch(requestUrl)
    .then((response) => {
      if (!response.ok) throw new Error(`Swatch fetch failed: ${response.status}`);
      return response.text();
    })
    .then((text) => JSON.parse(text.trim()))
    .catch(() => {
      cardSwatchCache.delete(requestUrl);
      return null;
    });

  cardSwatchCache.set(requestUrl, request);
  return request;
}

function findSwatchEntry(swatchData, valueName) {
  if (!swatchData || !valueName) return null;

  for (const optionValues of Object.values(swatchData)) {
    if (optionValues && optionValues[valueName]) {
      return optionValues[valueName];
    }
  }

  return null;
}

function applyCardSwatchVisual(swatchEl, entry) {
  if (!swatchEl || !entry) return;

  if (entry.image) {
    swatchEl.style.setProperty('--swatch-background-image', `url(${entry.image})`);
    swatchEl.classList.add('with-image');
  }

  if (entry.color) {
    swatchEl.style.setProperty('--swatch-background', entry.color);
  }
}

async function hydrateCardSwatches(root = document) {
  const groups = root.querySelectorAll('[data-swatch-product-url]');
  if (!groups.length) return;

  await Promise.all(
    [...groups].map(async (group) => {
      const productUrl = group.getAttribute('data-swatch-product-url');
      if (!productUrl) return;

      const swatchData = await fetchCardSwatches(productUrl);
      if (!swatchData) return;

      group.querySelectorAll('[data-swatch-value]').forEach((item) => {
        const valueName = item.getAttribute('data-swatch-value');
        const entry = findSwatchEntry(swatchData, valueName);
        applyCardSwatchVisual(item.querySelector('.color-swatch'), entry);
      });
    })
  );
}

function initCardSwatches(root) {
  hydrateCardSwatches(root);
}

document.addEventListener('shopify:section:load', (event) => {
  initKlaviyoBisTriggers(event.target);
  initCardSwatches(event.target);
});

if (window.theme?.pubsub?.subscribe) {
  theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.facetUpdate, () => {
    requestAnimationFrame(() => initCardSwatches());
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initCardSwatches(), { once: true });
} else {
  initCardSwatches();
}
